import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DemoScenario } from "@/types/demo";
import { toast } from "sonner";
import { log } from "@/utils/debug";
import { compareTexts } from "@/utils/textNormalization";

export const useDemoScenarios = () => {
  const queryClient = useQueryClient();

  const { data: scenarios, isLoading } = useQuery({
    queryKey: ["demo-scenarios"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("demo_scenarios")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        log.error("Error fetching demo scenarios:", error);
        throw error;
      }
      return data as DemoScenario[];
    },
  });

  const createScenario = useMutation({
    mutationFn: async (newScenario: Pick<DemoScenario, "demo_text" | "clinical_codes">) => {
      // Store the original text without normalization
      log.process("Creating scenario with raw text:", newScenario.demo_text);

      const { data, error } = await supabase
        .from("demo_scenarios")
        .insert({
          ...newScenario,
          demo_text: newScenario.demo_text, // Store raw text
        })
        .select()
        .single();

      if (error) {
        log.error("Error creating demo scenario:", error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["demo-scenarios"] });
      toast.success("Demo scenario created successfully");
    },
    onError: (error) => {
      log.error("Failed to create demo scenario:", error);
      toast.error(`Failed to create demo scenario: ${error.message}`);
    },
  });

  const updateScenario = useMutation({
    mutationFn: async (updatedScenario: Pick<DemoScenario, "id" | "demo_text" | "clinical_codes">) => {
      // Store the original text without normalization
      log.process("Updating scenario with raw text:", updatedScenario.demo_text);

      const { data, error } = await supabase
        .from("demo_scenarios")
        .update({
          demo_text: updatedScenario.demo_text, // Store raw text
          clinical_codes: updatedScenario.clinical_codes,
          updated_at: new Date().toISOString(),
        })
        .eq("id", updatedScenario.id)
        .select()
        .single();

      if (error) {
        log.error("Error updating demo scenario:", error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["demo-scenarios"] });
      toast.success("Demo scenario updated successfully");
    },
    onError: (error) => {
      log.error("Failed to update demo scenario:", error);
      toast.error(`Failed to update demo scenario: ${error.message}`);
    },
  });

  const deleteScenario = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("demo_scenarios").delete().eq("id", id);

      if (error) {
        log.error("Error deleting demo scenario:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["demo-scenarios"] });
    },
  });

  // Helper function to compare raw texts with normalization only for comparison
  const areSemanticallyEqual = (text1: string, text2: string): boolean => {
    log.process("Comparing texts semantically");
    return compareTexts(text1, text2, "Input text", "Stored text");
  };

  const findMatchingScenario = async (text: string): Promise<DemoScenario | null> => {
    // Don't normalize the input text for storage, just use it directly for comparison
    const rawInputText = text;

    log.process("Finding matching scenario for raw text:", {
      textLength: rawInputText.length,
      textSample: rawInputText.substring(0, 50) + (rawInputText.length > 50 ? "..." : ""),
    });

    // Simulate processing delay (2-5 seconds)
    await new Promise((resolve) => setTimeout(resolve, 2000 + Math.random() * 3000));

    // Get all scenarios to perform detailed comparisons
    const { data: allScenarios, error } = await supabase
      .from("demo_scenarios")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      log.error("Error finding matching scenario:", error);
      toast.error(`Error finding matching scenario: ${error.message}`);
      return null;
    }

    // Log all available scenarios for debugging
    log.process("Available scenarios to match against:", allScenarios?.length || 0);

    // Try to find a match using our semantically equal helper
    let matchedScenario = null;

    if (allScenarios && allScenarios.length > 0) {
      for (const scenario of allScenarios) {
        // Use the areSemanticallyEqual helper which normalizes only during comparison
        const isMatch = areSemanticallyEqual(rawInputText, scenario.demo_text);

        if (isMatch) {
          matchedScenario = scenario;
          log.process("Found matching scenario!", {
            id: scenario.id,
            textLength: scenario.demo_text.length,
          });
          break;
        }
      }
    }

    if (matchedScenario) {
      log.process("Returning matching scenario:", { id: matchedScenario.id });
      return matchedScenario;
    } else {
      log.process("No matching scenario found for the input text");
      toast.error("No matching scenario found for the entered text.");
      return null;
    }
  };

  return {
    scenarios,
    isLoading,
    createScenario,
    updateScenario,
    deleteScenario,
    findMatchingScenario,
  };
};
